import { useEffect } from 'react';
import { useConfig } from '@context/ConfigContext';

const SEO: React.FC = () => {
  const { config } = useConfig();

  useEffect(() => {
    if (!config) return;

    const { business, contact } = config;

    // Update document title (optimized for SEO - 50-60 characters)
    const titleName = business.name.replace('and', '&'); // Shorten for SEO
    document.title = `${titleName} - ${business.tagline}`;

    // Update meta description (optimized for SEO - 120-160 characters)
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        `Small acreage slashing specialist in New England NSW. Steep terrain, blackberry removal & difficult access areas. Call ${contact.phone} for a free quote.`
      );
    }

    // Update or create meta keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute(
      'content',
      'slashing services, mulching, land clearing, small acreage, steep terrain, blackberry removal, vegetation control, Armidale, Uralla, Guyra, New England Region, NSW, lifestyle block clearing, rural property maintenance, weed control, fire hazard reduction, firebreaks, fire prevention, acreage mowing, brush cutting, paddock slashing, blackberry control, overgrown paddock, difficult terrain, hard to access, small property specialist'
    );

    // Add or update canonical URL
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.href = contact.website ? `https://${contact.website}` : window.location.href;

    // Open Graph tags for social sharing
    const ogTags = [
      { property: 'og:type', content: 'website' },
      { property: 'og:title', content: `${titleName} - ${business.tagline}` },
      { property: 'og:description', content: `Professional slashing and mulching services in the New England Region. Specializing in small acreage properties and difficult terrain.` },
      { property: 'og:url', content: contact.website ? `https://${contact.website}` : window.location.href },
      { property: 'og:site_name', content: business.name },
      { property: 'og:locale', content: 'en_AU' },
      { property: 'og:image', content: `https://${contact.website || window.location.host}/assets/images/hero-01.jpg` },
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '630' },
    ];

    ogTags.forEach(({ property, content }) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    });

    // Twitter Card tags
    const twitterTags = [
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: `${titleName} - ${business.tagline}` },
      { name: 'twitter:description', content: `Professional slashing and mulching services in the New England Region. Specializing in small acreage properties and difficult terrain.` },
      { name: 'twitter:image', content: `https://${contact.website || window.location.host}/assets/images/hero-01.jpg` },
    ];

    twitterTags.forEach(({ name, content }) => {
      let tag = document.querySelector(`meta[name="${name}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('name', name);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    });

    // Add structured data (Schema.org JSON-LD)
    const existingScript = document.querySelector('script[type="application/ld+json"]');
    if (existingScript) {
      existingScript.remove();
    }

    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      '@id': contact.website ? `https://${contact.website}#business` : undefined,
      name: business.name,
      description: 'Professional slashing and mulching services specializing in small acreage properties with difficult terrain in the New England Region, NSW.',
      url: contact.website ? `https://${contact.website}` : undefined,
      telephone: contact.phone,
      priceRange: '$$',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Armidale',
        addressRegion: 'NSW',
        addressCountry: 'AU',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: -30.5131,
        longitude: 151.6650,
      },
      areaServed: [
        {
          '@type': 'City',
          name: 'Armidale',
          addressRegion: 'NSW',
          addressCountry: 'AU',
        },
        {
          '@type': 'City',
          name: 'Uralla',
          addressRegion: 'NSW',
          addressCountry: 'AU',
        },
        {
          '@type': 'City',
          name: 'Guyra',
          addressRegion: 'NSW',
          addressCountry: 'AU',
        },
        {
          '@type': 'City',
          name: 'Walcha',
          addressRegion: 'NSW',
          addressCountry: 'AU',
        },
        {
          '@type': 'City',
          name: 'Glen Innes',
          addressRegion: 'NSW',
          addressCountry: 'AU',
        },
      ],
      openingHours: 'Mo-Su',
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: contact.phone,
        contactType: 'Customer Service',
        areaServed: 'AU',
        availableLanguage: 'English',
      },
      sameAs: Object.values(config.social).filter(url => url),
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '5',
        reviewCount: config.components.testimonials.items.length,
      },
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(structuredData);
    document.head.appendChild(script);
  }, [config]);

  return null;
};

export default SEO;
